// @flow
/**
 * @module Epics/categoria
 * @desc Categoria
 */

import { ActionTypes } from '../utils/constants';
import { ofType, ActionsObservable } from 'redux-observable';
import { switchMap, map, catchError, concatMap } from 'rxjs/operators';
import { of } from 'rxjs';

import { BaseApiService } from '../utils/BaseApiService';
import {
  fetchInfoFromVersionColorSuccess,
  fetchInfoFromVersionColorFailure,
  selectAccessorySuccess,
  selectAccessoryFailure,
  selectMaintenanceSuccess,
  selectMaintenanceFailure,
  chooseInsuranceSuccess,
  chooseInsuranceFailure,
  changePrimePercentageSuccess,
  changePrimePercentageFailure,
  chooseFinancingOptionSuccess,
  chooseFinancingOptionFailure,
  changePlazoFailure,
  changePlazoSuccess,
  getClientInfoSuccess,
  getClientInfoFailure,
} from '../actions';

export function fetchInfoFromVersionColor(action$: ActionsObservable) {
  return action$.pipe(
    ofType(ActionTypes.FETCH_VERSION_COLOR_INFO_REQUEST),
    switchMap(action => {
      const { tipo, modelo, version, anio } = action.payload;

      return BaseApiService.get(
        `/api/cotizador/${tipo}/${modelo}/${version}/${anio}/opciones/`
      ).pipe(
        map(({ response }) => {
          return fetchInfoFromVersionColorSuccess(response);
        }),
        catchError(
          /* istanbul ignore next  */ ({ actions = [] }) =>
            of(fetchInfoFromVersionColorFailure(), ...actions)
        )
      );
    })
  );
}

export function selectAccessory(action$: ActionsObservable, store) {
  return action$.pipe(
    ofType(ActionTypes.SELECT_ACCESSORY_REQUEST),
    switchMap(action => {
      const accesoriosSeleccionados =
        store.value?.personalization?.data?.accesoriosSeleccionados || [];
      const payload = accesoriosSeleccionados.reduce(
        (reducedPayload, currentAccessory) => ({
          ...reducedPayload,
          [`accessories[${currentAccessory.id}]`]: 'on',
        }),
        {}
      );
      const { tipo, modelo, version, anio, color } = action.payload;

      return BaseApiService.post(
        `/api/cotizador/${tipo}/${modelo}/${version}/${anio}/${color}/accesorios/`,
        payload
      ).pipe(
        map(() => {
          return selectAccessorySuccess();
        }),
        catchError(
          /* istanbul ignore next  */ ({ actions = [] }) =>
            of(selectAccessoryFailure(), ...actions)
        )
      );
    })
  );
}

export function selectMaintenance(action$: ActionsObservable, store) {
  return action$.pipe(
    ofType(ActionTypes.SELECT_MAINTENANCE_REQUEST),
    switchMap(action => {
      const { maintenanceId, state } = action.payload;
      const { tipo, modelo, version, anio, color } = action.payload;

      const payload = {
        'mantenimiento[id]': maintenanceId,
      };
      if (state) {
        payload['mantenimiento[selected]'] = 'on';
      }
      return BaseApiService.post(
        `/api/cotizador/${tipo}/${modelo}/${version}/${anio}/${color}/mantenimientos/`,
        payload
      ).pipe(
        map(() => {
          return selectMaintenanceSuccess();
        }),
        catchError(
          /* istanbul ignore next  */ ({ actions = [] }) =>
            of(selectMaintenanceFailure(), ...actions)
        )
      );
    })
  );
}

export function chooseInsurance(action$: ActionsObservable, store) {
  return action$.pipe(
    ofType(ActionTypes.CHOOSE_INSURANCE_REQUEST),
    switchMap(action => {
      const segurosSeleccionados =
        store.value?.personalization?.data?.segurosSeleccionados || [];
      const { tipo, modelo, version, anio, color, insurance } = action.payload;
      const payload = {
        'seguro[id]': insurance?.id,
        ...(segurosSeleccionados.length && { 'seguro[selected]': 'on' }),
      };

      return BaseApiService.post(
        `/api/cotizador/${tipo}/${modelo}/${version}/${anio}/${color}/seguros/`,
        payload
      ).pipe(
        map(() => {
          return chooseInsuranceSuccess();
        }),
        catchError(
          /* istanbul ignore next  */ ({ actions = [] }) =>
            of(chooseInsuranceFailure(), ...actions)
        )
      );
    })
  );
}

export function changePrimePercentage(action$: ActionsObservable, store) {
  return action$.pipe(
    ofType(ActionTypes.CHANGE_PRIME_PERCENTAGE_REQUEST),
    concatMap(action => {
      const { tipo, modelo, version, anio, color, percentage } = action.payload;
      const payload = {
        porcPrima: percentage,
      };

      return BaseApiService.post(
        `/api/cotizador/${tipo}/${modelo}/${version}/${anio}/${color}/seguros/`,
        payload
      ).pipe(
        map(() => {
          return changePrimePercentageSuccess();
        }),
        catchError(
          /* istanbul ignore next  */ ({ actions = [] }) =>
            of(changePrimePercentageFailure(), ...actions)
        )
      );
    })
  );
}

export function changePlazoPercentage(action$: ActionsObservable, store) {
  return action$.pipe(
    ofType(ActionTypes.CHANGE_PLAZO_REQUEST),
    concatMap(action => {
      const { tipo, modelo, version, anio, color, plazo } = action.payload;
      const payload = {
        plazo,
      };
      return BaseApiService.post(
        `/api/cotizador/${tipo}/${modelo}/${version}/${anio}/${color}/seguros/`,
        payload
      ).pipe(
        map(() => {
          return changePlazoSuccess();
        }),
        catchError(
          /* istanbul ignore next  */ ({ actions = [] }) =>
            of(changePlazoFailure(), ...actions)
        )
      );
    })
  );
}

export function chooseFinancingOption(action$: ActionsObservable, store) {
  return action$.pipe(
    ofType(ActionTypes.CHOOSE_FINANCING_OPTION_REQUEST),
    concatMap(action => {
      const { tipo, modelo, version, anio, color } = action.payload;
      const opcionesSeleccionadas = (
        store.value?.personalization?.additional?.bancos || []
      ).filter(banco => banco.isSelected);
      const payload = opcionesSeleccionadas.reduce(
        (newPayload, currentOption, i) => {
          return { ...newPayload, [`bancos[${i}]`]: currentOption.post_type };
        },
        {}
      );

      return BaseApiService.post(
        `/api/cotizador/${tipo}/${modelo}/${version}/${anio}/${color}/seguros/`,
        payload
      ).pipe(
        map(() => {
          return chooseFinancingOptionSuccess();
        }),
        catchError(
          /* istanbul ignore next  */ ({ actions = [] }) =>
            of(chooseFinancingOptionFailure(), ...actions)
        )
      );
    })
  );
}

export function getClientInfo(action$: ActionsObservable) {
  return action$.pipe(
    ofType(ActionTypes.REQUEST_CLIENT_INFO),
    switchMap(action => {
      const { tipo = 't', modelo = 'm', version = 'v', anio = 2021, color = 'c', id, callback = () => {} } = action.payload;
      return BaseApiService.post(
        `/api/cotizador/${tipo}/${modelo}/${version}/${anio}/${color}/resumen/`,
        {
          'gometa': true,
          'id': id
        }
      ).pipe(
        map(({ response }) => {
          callback(response);
          return getClientInfoSuccess(response)
        }),
        catchError(
          /* istanbul ignore next  */ ({ actions = [] }) =>
          of(getClientInfoFailure(), ...actions)
        )
      );
    })
  );
}
